/**
 * PostHog Analytics Event Types and Constants
 *
 * This file defines all tracked events with typed properties for the Athletiverse Atlas application.
 */

// ============================================
// EVENT NAME CONSTANTS
// ============================================

export const AnalyticsEvents = {
  // ---- Authentication Events ----
  AUTH_LOGIN_SUCCESS: 'auth_login_success',
  AUTH_LOGIN_FAILED: 'auth_login_failed',
  AUTH_LOGOUT: 'auth_logout',
  AUTH_REGISTRATION_STARTED: 'auth_registration_started',
  AUTH_REGISTRATION_COMPLETED: 'auth_registration_completed',
  AUTH_REGISTRATION_FAILED: 'auth_registration_failed',
  AUTH_PASSWORD_RESET_REQUESTED: 'auth_password_reset_requested',
  AUTH_PASSWORD_RESET_COMPLETED: 'auth_password_reset_completed',
  AUTH_EMAIL_VERIFIED: 'auth_email_verified',
  AUTH_INVITATION_ACCEPTED: 'auth_invitation_accepted',

  // ---- Subscription/Billing Events ----
  SUBSCRIPTION_PLAN_VIEWED: 'subscription_plan_viewed',
  SUBSCRIPTION_PLAN_SELECTED: 'subscription_plan_selected',
  SUBSCRIPTION_CHECKOUT_STARTED: 'subscription_checkout_started',
  SUBSCRIPTION_CHECKOUT_COMPLETED: 'subscription_checkout_completed',
  SUBSCRIPTION_CHECKOUT_CANCELED: 'subscription_checkout_canceled',
  SUBSCRIPTION_UPGRADED: 'subscription_upgraded',
  SUBSCRIPTION_DOWNGRADED: 'subscription_downgraded',
  SUBSCRIPTION_CANCELED: 'subscription_canceled',
  SUBSCRIPTION_PAYMENT_METHOD_ADDED: 'subscription_payment_method_added',
  SUBSCRIPTION_PAYMENT_METHOD_REMOVED: 'subscription_payment_method_removed',

  // ---- Feature Usage Events ----
  DASHBOARD_VIEWED: 'dashboard_viewed',
  SOCIAL_MEDIA_ANALYTICS_VIEWED: 'social_media_analytics_viewed',
  REPORT_GENERATED: 'report_generated',
  REPORT_DOWNLOADED: 'report_downloaded',
  REPORT_GENERATION_FAILED: 'report_generation_failed',
  REPORT_SCHEDULE_CREATED: 'report_schedule_created',
  REPORT_SCHEDULE_UPDATED: 'report_schedule_updated',
  REPORT_SCHEDULE_DELETED: 'report_schedule_deleted',
  REPORT_SCHEDULE_STATUS_TOGGLED: 'report_schedule_status_toggled',
  REPORT_SCHEDULE_RUN_NOW: 'report_schedule_run_now',
  FILTER_APPLIED: 'filter_applied',
  DATE_RANGE_CHANGED: 'date_range_changed',
  PLATFORM_SELECTED: 'platform_selected',
  POST_INSIGHTS_VIEWED: 'post_insights_viewed',
  BRAND_DETAILS_VIEWED: 'brand_details_viewed',
  COMPETITIVE_ANALYSIS_VIEWED: 'competitive_analysis_viewed',
  AI_INTELLIGENCE_USED: 'ai_intelligence_used',

  // ---- Navigation Events ----
  SIDEBAR_NAVIGATION: 'sidebar_navigation',
  DRAWER_OPENED: 'drawer_opened',
  DRAWER_CLOSED: 'drawer_closed',
  TAB_CHANGED: 'tab_changed',
  SETTINGS_TAB_VIEWED: 'settings_tab_viewed',

  // ---- User Management Events ----
  USER_INVITED: 'user_invited',
  USER_ROLE_CHANGED: 'user_role_changed',
  USER_REMOVED: 'user_removed',

  // ---- Account/Profile Events ----
  PROFILE_UPDATED: 'profile_updated',
  ORGANIZATION_UPDATED: 'organization_updated',
  SOCIAL_ACCOUNT_CONNECTED: 'social_account_connected',
  SOCIAL_ACCOUNT_DISCONNECTED: 'social_account_disconnected',
} as const;

export type AnalyticsEventName = (typeof AnalyticsEvents)[keyof typeof AnalyticsEvents];

// ============================================
// EVENT PROPERTY INTERFACES
// ============================================

export interface AuthEventProperties {
  method?: 'email' | 'sso';
  rememberMe?: boolean;
  errorCode?: string;
  errorMessage?: string;
}

export interface SubscriptionEventProperties {
  tierId?: number;
  tierName?: string;
  billingCycle?: 'monthly' | 'annual';
  previousTier?: string;
  newTier?: string;
  amount?: number;
  currency?: string;
  discountApplied?: boolean;
  cancellationReason?: string;
}

export interface DashboardEventProperties {
  customerId?: string;
  period?: string;
  platformIds?: string[];
  postType?: string;
  view?: string;
}

export interface NavigationEventProperties {
  from?: string;
  to?: string;
  view?: string;
  tabId?: string;
}

export interface FilterEventProperties {
  filterType: string;
  filterValue: string | string[];
  customerId?: string;
}

export interface ReportEventProperties {
  reportType: string;
  format?: 'pdf' | 'csv' | 'xlsx';
  dateRange?: string;
  customerId?: string;
  scheduleId?: string;
  scheduleType?: 'one-time' | 'recurring';
  frequency?: 'daily' | 'weekly' | 'monthly';
  emailEnabled?: boolean;
  newStatus?: 'active' | 'paused';
  errorMessage?: string;
}

export interface DrawerEventProperties {
  drawerType: 'post_insights' | 'brand_details';
  postId?: string;
  brandId?: string;
  brandName?: string;
}

// Union type for all event properties
export type AnalyticsEventProperties =
  | AuthEventProperties
  | SubscriptionEventProperties
  | DashboardEventProperties
  | NavigationEventProperties
  | FilterEventProperties
  | ReportEventProperties
  | DrawerEventProperties
  | Record<string, unknown>;

// ============================================
// USER IDENTIFICATION PROPERTIES
// ============================================

export interface PostHogUserProperties {
  email: string;
  firstName: string;
  lastName: string;
  organizationId: string;
  organizationName?: string;
  roleName: string;
  subscriptionTier?: string;
  subscriptionStatus?: string;
  customerId?: string;
  onboardingStatus?: string;
}
